import React, { useState, useContext } from "react";
import { Modal, Input, Select, message } from "antd";
import { AuthContext } from "../../../contexts/AuthContext";
import { useQv4Theme } from "../../helpers";
import { useQueryLifecycle } from "../../viewmodels/modals-misc/useQueryLifecycle";

// Canonical pipeline order for computing which stages are "earlier". Ops
// statuses (waiting_purchase → out_for_delivery) sit between paid and
// completed — see pipeline.js bucketing and the Operations page. Draft is
// deliberately not a target: moving back to draft would pretend the
// supplier send never happened. Completed/cancelled/parked aren't listed,
// so cards in those states get no move-back option (unpark and warranty
// flows own those corrections).
const STATUS_ORDER = [
  "waiting_for_price",
  "waiting_customer_confirmation",
  "confirmed_waiting_payment",
  "paid",
  "waiting_purchase",
  "purchased",
  "out_for_delivery",
];

const STATUS_LABELS = {
  waiting_for_price: "Waiting For Price",
  waiting_customer_confirmation: "Waiting Confirmation",
  confirmed_waiting_payment: "Confirmed · Awaiting Payment",
  paid: "Paid",
  waiting_purchase: "Waiting Purchase (Ops)",
  purchased: "Purchased (Ops)",
  out_for_delivery: "Out For Delivery (Ops)",
};

export function movableBackStatuses(currentStatus) {
  const idx = STATUS_ORDER.indexOf(currentStatus);
  if (idx <= 0) return [];
  return STATUS_ORDER.slice(0, idx);
}

export function MoveStatusModal({ open, queryPk, queryId, currentStatus, onClose, onSuccess }) {
  const { authState } = useContext(AuthContext);
  const [toStatus, setToStatus] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const { t } = useQv4Theme();
  const { moveBack } = useQueryLifecycle(queryPk, authState?.id);

  const options = movableBackStatuses(currentStatus)
    .map(s => ({ value: s, label: STATUS_LABELS[s] ?? s }))
    .reverse(); // nearest stage first — that's the usual correction

  const handleClose = () => {
    setToStatus(null);
    setReason("");
    onClose();
  };

  const handleOk = async () => {
    if (!toStatus) { message.warning("Select the stage to move back to"); return; }
    if (!reason.trim()) { message.warning("A reason is required — it becomes part of the task's movement history"); return; }
    setSaving(true);
    try {
      const res = await moveBack(toStatus, reason.trim());
      if (!res?.success) throw new Error(res?.message);
      message.success(`Task moved back to ${STATUS_LABELS[toStatus] ?? toStatus}`);
      onSuccess?.();
      handleClose();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to move task");
    } finally { setSaving(false); }
  };

  return (
    <Modal
      open={open}
      title={`Move Task Back${queryId ? ` — #${queryId}` : ""}`}
      onCancel={handleClose}
      onOk={handleOk}
      confirmLoading={saving}
      okText="Move Back"
      cancelText="Go Back"
      destroyOnHidden
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
        <div style={{ fontSize: 13, color: t.textSub }}>
          Currently in <b>{STATUS_LABELS[currentStatus] ?? currentStatus}</b>. The move is
          recorded in the task's movement history with your name and reason. Payments and
          quotes are never deleted by this — correct money via a new payment/refund entry.
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: t.textSub }}>
            Move back to <span style={{ color: "#ff4d4f" }}>*</span>
          </div>
          <Select
            options={options}
            value={toStatus}
            onChange={setToStatus}
            placeholder="Select earlier stage"
            style={{ width: "100%" }}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: t.textSub }}>
            Reason <span style={{ color: "#ff4d4f" }}>*</span>
          </div>
          <Input.TextArea
            rows={3}
            placeholder="e.g. Wrong quote sent — customer needs a corrected payment link"
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
